<script setup lang="ts">
import { type ShiftTodayListItem } from '@/api/shifts';
import { getCareerColor } from '@/utils/careers';

// Phone rendering of a /today_shifts row: tap anywhere opens the shift
// detail; attendance actions stop propagation so they never double-fire.
interface Props {
  shift: ShiftTodayListItem;
  canMarkAttendance: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  open: [];
  'check-in': [];
  'check-out': [];
}>();

function showActions() {
  return props.canMarkAttendance &&
    (props.shift.canCheckInByAdmin || props.shift.canCheckOutByAdmin);
}
</script>
<template>
  <v-card
    v-ripple
    variant="elevated"
    :elevation="1"
    class="pa-3"
    @click="emit('open')"
  >
    <div class="d-flex align-center ga-2">
      <span class="font-weight-bold text-body-1 text-truncate flex-grow-1">
        {{ shift.patientFullName }}
      </span>
      <v-icon
        v-if="shift.hasIncidents"
        icon="mdi-alert"
        color="error"
        size="small"
      />
      <v-chip
        :color="shift.status.color"
        size="small"
        class="flex-shrink-0"
      >
        {{ shift.status.name }}
      </v-chip>
    </div>
    <div class="d-flex align-center ga-2 mt-1">
      <v-icon
        icon="mdi-circle"
        :color="getCareerColor(shift.caregiverCareer)"
        size="x-small"
      />
      <span class="text-body-2 text-truncate">{{ shift.caregiverFullName }}</span>
      <span class="text-caption text-medium-emphasis flex-shrink-0">
        {{ shift.caregiverHumanCareer }}
      </span>
    </div>
    <div class="d-flex align-center flex-wrap ga-3 mt-1 text-body-2">
      <span>
        <v-icon
          icon="mdi-clock-outline"
          size="small"
          class="mr-1"
        />{{ shift.humanTime }}
      </span>
      <span v-if="shift.checkIn?.humanTime">
        Inicio {{ shift.checkIn.humanTime }}
      </span>
      <span v-if="shift.checkOut?.humanTime">
        Término {{ shift.checkOut.humanTime }}
      </span>
    </div>
    <div
      v-if="showActions()"
      class="d-flex ga-2 mt-2"
    >
      <v-btn
        v-if="shift.canCheckInByAdmin"
        size="small"
        variant="tonal"
        prepend-icon="mdi-location-enter"
        @click.stop="emit('check-in')"
      >
        {{ shift.hasEnded ? 'Marcar Terminado' : 'Marcar Inicio' }}
      </v-btn>
      <v-btn
        v-if="shift.canCheckOutByAdmin"
        size="small"
        variant="tonal"
        prepend-icon="mdi-location-exit"
        @click.stop="emit('check-out')"
      >
        Marcar Término
      </v-btn>
    </div>
  </v-card>
</template>
